import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Avatar,
  Badge,
  Dropdown,
  Button,
  Typography,
  message,
} from "antd";

import {
  UserOutlined,
  ProfileOutlined,
  LogoutOutlined,
  CameraOutlined,
  MessageOutlined,
  RightOutlined,
} from "@ant-design/icons";

import api, { BASE_URL } from "../api/api";
import AddAcademyYear from "./AcademyYearSelection/AddAcademyYear";
import AdminPublishGradesPopup from "../SchoolDashApp/AdminPublishGradesPopup";
import { Link } from "react-router-dom";

const { Text } = Typography;

const PROFILE_UPDATED_EVENT = "profileUpdated";

const ProfileImageTeacher = ({
  theme,
  header = false,
  collapsed = false,
}) => {
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  const [openAcademyYear, setOpenAcademyYear] =
    useState(false);

  const [openPublishGrades, setOpenPublishGrades] =
    useState(false);

  const fetchAcademicYears = async () => {
    try {
      await api.get("/academic-years/");

      window.dispatchEvent(
        new Event("academicYearUpdated")
      );
    } catch (error) {
      console.log(
        "Academic years fetch error:",
        error
      );
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get("/user-profile/");
      setUser(res.data);
    } catch (error) {
      console.log(
        "Profile fetch error:",
        error
      );
    }
  };

  useEffect(() => {
    fetchProfile();

    const updateProfile = () => {
      fetchProfile();
    };

    window.addEventListener(
      PROFILE_UPDATED_EVENT,
      updateProfile
    );

    return () => {
      window.removeEventListener(
        PROFILE_UPDATED_EVENT,
        updateProfile
      );
    };
  }, []);

  const getProfileImage = () => {
    if (!user?.profile_image) {
      return null;
    }

    if (
      user.profile_image.startsWith("http")
    ) {
      return user.profile_image;
    }

    return `${BASE_URL}${user.profile_image}`;
  };

  const openFilePicker = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      message.error(
        "Please select a valid image file."
      );

      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error(
        "Profile image must be 5MB or smaller."
      );

      e.target.value = "";
      return;
    }

    const formData = new FormData();

    formData.append(
      "profile_image",
      file
    );

    try {
      setUploading(true);

      const res = await api.put(
        "/user-profile/",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setUser(res.data);

      window.dispatchEvent(
        new Event(PROFILE_UPDATED_EVENT)
      );

      message.success(
        "Profile image updated successfully."
      );
    } catch (error) {
      console.log(
        "Profile image update error:",
        error
      );

      message.error(
        "Profile image update failed."
      );
    } finally {
      setUploading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/api/logout/");

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");

      window.location.href = "/login";
    } catch (error) {
      console.log(
        "Logout error:",
        error
      );
    }
  };

  const handleMenuClick = ({ key }) => {
    if (key === "logout") {
      handleLogout();
    }
  };

  const menuItems = [
    {
      key: "profile",
      icon: <ProfileOutlined />,
      label: "My Profile",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      danger: true,
    },
  ];

  const profileMenu = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  const profileImage = getProfileImage();

  /*
  ============================================================
  SHARED PROFILE IMAGE
  ============================================================
  */

  const ProfileAvatar = ({
    size = 58,
    bordered = true,
  }) => (
    <Badge
      dot
      status={
        user?.is_online
          ? "success"
          : "default"
      }
      offset={
        size >= 50
          ? [-3, size - 12]
          : [-2, size - 9]
      }
    >
      <Avatar
        size={size}
        src={profileImage}
        icon={<UserOutlined />}
        style={{
          border: bordered
            ? `2px solid ${theme.primary}`
            : "none",

          background:
            theme.card,

          color:
            theme.primary,

          flexShrink: 0,

          boxShadow:
            size >= 50
              ? `0 5px 16px ${theme.primary}20`
              : "none",
        }}
      />
    </Badge>
  );

  /*
  ============================================================
  HEADER PROFILE
  ============================================================
  */

  if (header) {
    return (
      <>
        <Dropdown
          placement="bottomRight"
          menu={profileMenu}
          trigger={["click"]}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              cursor: "pointer",
              padding: "3px 5px",
              borderRadius: 12,
              minWidth: 0,
              transition:
                "background .2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                theme.darkMode
                  ? "rgba(255,255,255,.05)"
                  : "#f5f8fc";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "transparent";
            }}
          >
            <ProfileAvatar
              size={40}
            />

            <div
              style={{
                display: "flex",
                flexDirection:
                  "column",
                lineHeight: 1.2,
                minWidth: 0,
                maxWidth: 145,
              }}
            >
              <Text
                strong
                ellipsis
                style={{
                  color: theme.text,
                  fontSize: 12,
                  maxWidth: 145,
                }}
              >
                {user?.username ||
                  "Teacher"}
              </Text>

              <Text
                ellipsis
                style={{
                  color:
                    theme.secondary,
                  fontSize: 10,
                  marginTop: 2,
                  textTransform:
                    "capitalize",
                }}
              >
                {user?.user_type ||
                  "Teacher"}
              </Text>
            </div>
          </div>
        </Dropdown>

        <AddAcademyYear
          open={openAcademyYear}
          handleClose={() => {
            setOpenAcademyYear(false);
          }}
          refreshYears={
            fetchAcademicYears
          }
        />

        <AdminPublishGradesPopup
          open={openPublishGrades}
          onClose={() =>
            setOpenPublishGrades(
              false
            )
          }
        />
      </>
    );
  }

  /*
  ============================================================
  COLLAPSED SIDEBAR PROFILE
  ============================================================
  */

  if (collapsed) {
    return (
      <>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent:
              "center",
            padding:
              "12px 0 14px",
          }}
        >
          <Dropdown
            placement="rightTop"
            menu={profileMenu}
            trigger={["click"]}
          >
            <div
              style={{
                position: "relative",
                width: 48,
                height: 48,
                cursor: "pointer",
              }}
              title="Teacher Profile"
            >
              <ProfileAvatar
                size={46}
              />

              <button
                type="button"
                onClick={
                  openFilePicker
                }
                disabled={uploading}
                aria-label="Change profile image"
                title={
                  uploading
                    ? "Uploading..."
                    : "Change profile image"
                }
                style={{
                  position:
                    "absolute",
                  right: -3,
                  bottom: -3,
                  width: 20,
                  height: 20,
                  borderRadius:
                    "50%",
                  border:
                    `2px solid ${
                      theme.sidebar
                    }`,
                  background:
                    theme.primary,
                  color:
                    "#ffffff",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  padding: 0,
                  cursor: uploading
                    ? "wait"
                    : "pointer",
                  boxShadow:
                    "0 3px 8px rgba(0,0,0,.20)",
                  zIndex: 5,
                }}
              >
                <CameraOutlined
                  style={{
                    fontSize: 9,
                  }}
                />
              </button>
            </div>
          </Dropdown>
        </div>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={
            handleImageChange
          }
          style={{
            display: "none",
          }}
        />

        <AddAcademyYear
          open={openAcademyYear}
          handleClose={() =>
            setOpenAcademyYear(
              false
            )
          }
          refreshYears={
            fetchAcademicYears
          }
        />

        <AdminPublishGradesPopup
          open={openPublishGrades}
          onClose={() =>
            setOpenPublishGrades(
              false
            )
          }
        />
      </>
    );
  }

  /*
  ============================================================
  EXPANDED SIDEBAR PROFILE
  ============================================================
  */

  return (
    <>
      <Dropdown
        placement="bottomRight"
        menu={profileMenu}
        trigger={["click"]}
      >
        <div
          style={{
            width: "100%",
            padding:
              "12px 12px 10px",
            margin:
              "8px 0 8px",
            borderRadius: 15,
            background:
              theme.darkMode
                ? "linear-gradient(145deg,#172235,#111827)"
                : "linear-gradient(145deg,#ffffff,#f7faff)",
            border:
              `1px solid ${theme.border}`,
            boxShadow:
              theme.darkMode
                ? "0 7px 20px rgba(0,0,0,.15)"
                : "0 5px 17px rgba(15,23,42,.055)",
            cursor: "pointer",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              gap: 11,
              width: "100%",
            }}
          >
            <div
              style={{
                position:
                  "relative",
                flexShrink: 0,
                width: 58,
                height: 58,
              }}
            >
              <ProfileAvatar
                size={58}
              />

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={
                  handleImageChange
                }
                style={{
                  display: "none",
                }}
              />

              <button
                type="button"
                onClick={
                  openFilePicker
                }
                disabled={uploading}
                aria-label="Change profile image"
                title={
                  uploading
                    ? "Uploading..."
                    : "Change profile image"
                }
                style={{
                  position:
                    "absolute",
                  right: -4,
                  bottom: -2,
                  width: 24,
                  height: 24,
                  borderRadius:
                    "50%",
                  border:
                    `2px solid ${
                      theme.darkMode
                        ? "#172235"
                        : "#ffffff"
                    }`,
                  background:
                    theme.primary,
                  color:
                    "#ffffff",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  padding: 0,
                  cursor: uploading
                    ? "wait"
                    : "pointer",
                  boxShadow:
                    "0 3px 8px rgba(0,0,0,.20)",
                  zIndex: 10,
                }}
              >
                <CameraOutlined
                  style={{
                    fontSize: 11,
                  }}
                />
              </button>
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Text
                strong
                ellipsis
                style={{
                  display: "block",
                  color:
                    theme.text,
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                {user?.username ||
                  "Loading..."}
              </Text>

              <Text
                ellipsis
                style={{
                  display: "block",
                  color:
                    theme.secondary,
                  fontSize: 10,
                  marginTop: 3,
                  lineHeight: 1.3,
                }}
              >
                {user?.email ||
                  "Teacher account"}
              </Text>

              <div
                style={{
                  marginTop: 6,
                  display:
                    "inline-flex",
                  alignItems:
                    "center",
                  maxWidth:
                    "100%",
                  padding:
                    "2px 7px",
                  borderRadius: 20,
                  background:
                    `${theme.primary}15`,
                  color:
                    theme.primary,
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform:
                    "capitalize",
                  whiteSpace:
                    "nowrap",
                  overflow:
                    "hidden",
                  textOverflow:
                    "ellipsis",
                }}
              >
                {user?.user_type ||
                  "Teacher"}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop:
                `1px solid ${theme.border}`,
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "space-between",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems:
                  "center",
                gap: 5,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  minWidth: 6,
                  borderRadius:
                    "50%",
                  background:
                    user?.is_online
                      ? "#22c55e"
                      : theme.muted,
                  boxShadow:
                    user?.is_online
                      ? "0 0 0 3px rgba(34,197,94,.10)"
                      : "none",
                }}
              />

              <Text
                style={{
                  color:
                    user?.is_online
                      ? "#16a34a"
                      : theme.muted,
                  fontSize: 9,
                  fontWeight: 600,
                }}
              >
                {user?.is_online
                  ? "Online"
                  : "Offline"}
              </Text>
            </div>

            <Text
              style={{
                color:
                  theme.muted,
                fontSize: 9,
              }}
            >
              My Profile
              <RightOutlined
                style={{
                  fontSize: 8,
                  marginLeft: 4,
                }}
              />
            </Text>
          </div>
        </div>
      </Dropdown>

      {[
        "admin",
        "registry",
        "fiscal affairs",
        "principal",
        "vpi",
        "vpa",
        "dean",
        "it personel",
      ].includes(
        user?.user_type
      ) && (
        <Link
          to="/chatApp"
          style={{
            textDecoration: "none",
            display: "block",
            width: "100%",
            marginBottom: 8,
          }}
        >
          <Button
            type="default"
            icon={
              <MessageOutlined />
            }
            block
            style={{
              height: 36,
              borderRadius: 9,
              borderColor:
                theme.border,
              background:
                theme.darkMode
                  ? "#172235"
                  : "#ffffff",
              color: theme.text,
              fontWeight: 600,
              fontSize: 11,
              boxShadow: "none",
            }}
          >
            Open Chats
          </Button>
        </Link>
      )}

      <AddAcademyYear
        open={openAcademyYear}
        handleClose={() =>
          setOpenAcademyYear(
            false
          )
        }
        refreshYears={
          fetchAcademicYears
        }
      />

      <AdminPublishGradesPopup
        open={openPublishGrades}
        onClose={() =>
          setOpenPublishGrades(
            false
          )
        }
      />
    </>
  );
};

export default ProfileImageTeacher;


